import axiosInstance from './axiosInstance';
import { tokenService } from './tokenService';

export type BillingIntent = 'upgrade' | 'credit_topup' | 'api_wallet_topup';

export interface BillingAttributionContext {
  source: string;
  surface: string;
  intent: BillingIntent;
}

interface CheckoutLink {
  payment_id?: string;
  payment_provider?: 'razorpay' | 'stripe' | 'apple';
  order_id?: string;
  session_id?: string;
  subscription_id?: string;
  provider_reference?: string;
}

let activeContext: (BillingAttributionContext & { setAt: number }) | null = null;
let activeJourneyId: string | null = null;
const ATTRIBUTION_TIMEOUT_MS = 1000;

const uuid = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : ((random & 0x3) | 0x8);

    return value.toString(16);
  });
};

const routeSource = (): string => {
  const path = typeof window === 'undefined' ? 'unknown' : window.location.pathname;

  return `route:${path || '/'}`;
};

const postQuietly = async (path: string, payload: object): Promise<void> => {
  if (!tokenService.getAccessToken()) return;
  try {
    await axiosInstance.post(path, payload, { timeout: ATTRIBUTION_TIMEOUT_MS });
  } catch {
    // Analytics must never block the purchase path.
  }
};

export const billingAttribution = {
  expose(context: BillingAttributionContext): void {
    void postQuietly('/api/v1/billing-attribution/events', {
      event_id: uuid(),
      event_type: 'impression',
      ...context,
    });
  },

  click(context: BillingAttributionContext): void {
    const navigationOrigin = activeContext
      && activeContext.surface === 'navigation'
      && Date.now() - activeContext.setAt < 30 * 60 * 1000
      ? activeContext.source
      : null;
    activeContext = { ...context, source: navigationOrigin || context.source, setAt: Date.now() };
    void postQuietly('/api/v1/billing-attribution/events', {
      event_id: uuid(),
      event_type: 'click',
      ...context,
    });
  },

  setContext(context: BillingAttributionContext): void {
    activeContext = { ...context, setAt: Date.now() };
  },

  markNavigation(source: string, intent: BillingIntent = 'upgrade'): void {
    const context = { source, surface: 'navigation', intent };
    activeContext = { ...context, setAt: Date.now() };
    void postQuietly('/api/v1/billing-attribution/events', {
      event_id: uuid(), event_type: 'click', ...context,
    });
  },

  async startCheckout(details: Partial<BillingAttributionContext> & {
    target_tier?: string;
    product_id?: string;
    credits?: number;
  }): Promise<string> {
    const recent = activeContext && Date.now() - activeContext.setAt < 30 * 60 * 1000
      ? activeContext
      : null;
    const journeyId = uuid();
    activeJourneyId = journeyId;
    const context = {
      source: details.source || recent?.source || routeSource(),
      surface: details.surface || recent?.surface || 'direct-checkout',
      intent: details.intent || recent?.intent || 'credit_topup',
    };
    await postQuietly('/api/v1/billing-attribution/checkout-start', {
      journey_id: journeyId,
      ...context,
      target_tier: details.target_tier,
      product_id: details.product_id,
      credits: details.credits,
    });

    return journeyId;
  },

  linkCheckout(journeyId: string, result: CheckoutLink): Promise<void> {
    const providerReference = result.provider_reference || result.session_id || result.order_id || result.subscription_id;
    void postQuietly('/api/v1/billing-attribution/checkout-link', {
      journey_id: journeyId,
      provider: result.payment_provider,
      provider_reference: providerReference || undefined,
      payment_id: result.payment_id || undefined,
    });

    return Promise.resolve();
  },

  linkVerifiedProviderReference(providerReference: string): Promise<void> {
    if (!activeJourneyId) return Promise.resolve();
    void postQuietly('/api/v1/billing-attribution/checkout-link', {
      journey_id: activeJourneyId,
      provider: 'razorpay',
      provider_reference: providerReference,
    });

    return Promise.resolve();
  },
};
